import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, DisplayPosition, withCitrusTheme, ZIndex } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { Basket } from '@features/basket'
import { ScrollableBasket } from '@features/scrollable-basket'
import type { Recipe, RecipeCoreId } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { incrementTutorialViewed } from 'actions/tutorial'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import type { NumPersonsToBoxDescriptors } from 'routes/BoxPrices/boxPricesModels'
import { getNumPersonsToBoxDescriptors } from 'routes/BoxPrices/boxPricesSelectors'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { useScrollableBasket } from 'routes/Menu/hooks/useScrollableBasket'
import { getShowBasketTooltip } from 'routes/Menu/selectors/basket'
import {
  getBasketDate,
  getBasketOrderId,
  getBasketRecipePositions,
  getBasketSlotId,
} from 'selectors/basket'
import { getBrowserType } from 'selectors/browser'
import { createGetActionTypeIsPending } from 'selectors/status'

import { useCheckoutButton } from '../../hooks/useCheckoutButton'
import { ViewBasketModal } from '../ScrollableBasket/ViewBasketModal'

const Container = withCitrusTheme(Box, () => ({
  margin: '0 auto',
}))

const BlurredBackground = withCitrusTheme(Box, () => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(30px)',
}))

const useRemoveNotLoadedRecipesEffect = () => {
  const { basketRecipes } = useBasketRecipes()
  const { removeRecipe } = useBasket()

  const { request } = useMenuMeta()
  const basketDate = useSelector(getBasketDate)

  useEffect(() => {
    if (!request || !basketDate) return

    const isCurrentMenuLoaded = basketDate === request.deliveryDate
    if (!isCurrentMenuLoaded) {
      return
    }

    basketRecipes.forEach(({ coreId, recipe }) => {
      if (!recipe) {
        removeRecipe(coreId)
      }
    })
  }, [removeRecipe, basketRecipes, request, basketDate])
}

export const BasketContainer = () => {
  const isLoading = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))
  const { isCheckoutButtonDisabled, isCheckoutButtonPending } = useCheckoutButton()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)
  const { basketRecipes } = useBasketRecipes()
  const recipePositions = useSelector(getBasketRecipePositions)
  const slotId = useSelector(getBasketSlotId)
  const basketOrderId = useSelector(getBasketOrderId)
  const basketDate = useSelector(getBasketDate)
  const isTooltipSeen = useSelector(getShowBasketTooltip)
  const isScrollableBasketEnabled = useScrollableBasket()
  const [viewBasketModalOpen, setViewBasketModalOpen] = useState(false)

  const { removeRecipe, recipeCount, getQuantitiesForRecipeId } = useBasket()

  const selectedRecipes = useMemo<Recipe[]>(() => {
    const recipes: Recipe[] = []

    basketRecipes.forEach(({ coreId, recipe }) => {
      if (recipe) {
        const quantity = getQuantitiesForRecipeId(coreId)
        for (let i = 0; i < quantity; i++) {
          recipes.push(recipe)
        }
      }
    })

    return recipes
  }, [basketRecipes, getQuantitiesForRecipeId])

  const sortedSelectedRecipes = useMemo(() => {
    if (!recipePositions?.size) {
      return selectedRecipes
    }

    // we have to use coreId because recipePositions are coreId, which is from BASKET_RECIPE_ADD action
    const recipesByCoreId = new Map(selectedRecipes.map((recipe) => [recipe.coreId, recipe]))

    // Build ordered recipes based on recipePosition
    const orderedRecipes: Recipe[] = recipePositions
      .map((coreId: string) => recipesByCoreId.get(coreId as RecipeCoreId))
      .filter((r: Recipe | undefined) => Boolean(r))
      .toArray()

    // Count how many times each recipe has been placed
    const placedCounts = orderedRecipes.reduce<Record<string, number>>((acc, recipe) => {
      acc[recipe.coreId] = (acc[recipe.coreId] ?? 0) + 1

      return acc
    }, {})

    // Filter out recipes already accounted for in orderedRecipes
    const leftoverRecipes = selectedRecipes.filter(({ coreId }) => {
      if (placedCounts[coreId]) {
        placedCounts[coreId] -= 1

        return false
      }

      return true
    })

    return [...leftoverRecipes, ...orderedRecipes]
  }, [recipePositions, selectedRecipes])

  useRemoveNotLoadedRecipesEffect()

  const boxPrices = useSelector(getNumPersonsToBoxDescriptors) as NumPersonsToBoxDescriptors
  const boxPricesForNumPortions = boxPrices?.[numPortions] || []
  const pricesPerServing = [
    '', // Placeholder for the first recipe
    ...boxPricesForNumPortions.map((prices) => prices.price_per_portion),
  ]

  const dispatch = useDispatch()
  const { pricing } = usePricing()
  const view = useSelector(getBrowserType)
  const section = trackingKeys.menu

  const handleCheckout = useCallback(
    (e) => {
      e.stopPropagation()
      if (!isCheckoutButtonPending) {
        datadogRum.addAction('basket_checkout_button_clicked', {
          slotId,
          basketDate,
          basketOrderId,
        })
        dispatch(checkoutBasket({ section, view, pricing, removeRecipe }))
      }
    },
    [
      dispatch,
      section,
      view,
      pricing,
      removeRecipe,
      isCheckoutButtonPending,
      slotId,
      basketDate,
      basketOrderId,
    ],
  )

  const handleRecipeClick = useCallback(
    (recipeUuid) => dispatch(showDetailRecipe(recipeUuid)),
    [dispatch],
  )

  // If there are no recipes in the basket, don't show the basket
  if (recipeCount <= 0 || isLoading) {
    return null
  }

  return (
    <BlurredBackground
      bottom="0"
      left="0"
      position={DisplayPosition.Fixed}
      width="100%"
      zIndex={ZIndex.Sticky}
    >
      <Container maxWidth="1600px">
        {isScrollableBasketEnabled && viewBasketModalOpen && (
          <ViewBasketModal
            onCloseModal={() => setViewBasketModalOpen(false)}
            onCheckout={handleCheckout}
          />
        )}
        {isScrollableBasketEnabled ? (
          <ScrollableBasket
            isCheckoutDisabled={isCheckoutButtonDisabled}
            isLoading={isLoading}
            onClickViewBasket={() => setViewBasketModalOpen(true)}
            onRecipeClick={(recipeUuid) => handleRecipeClick(recipeUuid)}
            selectedRecipes={sortedSelectedRecipes}
            showTooltip={!isTooltipSeen}
            onCloseTooltip={() => dispatch(incrementTutorialViewed('basket-tooltip'))}
          />
        ) : (
          <Basket
            isCheckoutDisabled={isCheckoutButtonDisabled}
            isLoading={isLoading}
            maxNumberRecipes={maxRecipesNum}
            onCheckout={handleCheckout}
            onRecipeClick={(recipeUuid) => handleRecipeClick(recipeUuid)}
            pricesPerServing={pricesPerServing}
            selectedRecipes={sortedSelectedRecipes}
            totalPrice={pricing?.total}
          />
        )}
      </Container>
    </BlurredBackground>
  )
}
